/**
 * Financial Forecasting Service
 * LivePlan-style financial projections with what-if scenarios
 * Pure calculation functions - no side effects
 */

import {
  FinancialForecast,
  FinancialProjection,
  ScenarioProjections,
  FinancialMetrics,
  FinancialAssumptions,
  RevenueStream,
  ExpenseCategory,
  ForecastCalculationInput,
  ScenarioType,
  IndustryBenchmark,
  TimelineType
} from '../types/financialForecasting';
import { supabase } from '../lib/supabaseClient';

type CapexAssetState = {
  id: string;
  name: string;
  amount: number;
  monthlyDepreciation: number;
  remainingMonths: number;
};

function mapForecastRow(row: any): FinancialForecast {
  return {
    id: String(row.id),
    business_plan_id: String(row.business_plan_id),
    user_id: String(row.user_id),
    timeline: row.timeline,
    periodsCount: row.periods_count,
    startDate: row.start_date,
    assumptions: row.assumptions || {},
    revenueStreams: row.revenue_streams || [],
    expenseCategories: row.expense_categories || [],
    projections: row.projections || { realistic: [], optimistic: [], pessimistic: [] },
    metrics: row.metrics || {},
    industryBenchmarks: row.industry_benchmarks || [],
    created_at: row.created_at,
    updated_at: row.updated_at,
    last_calculated_at: row.last_calculated_at,
  };
}

function toForecastUpsertRow(args: {
  businessPlanId: string;
  userId: string;
  forecast: Omit<FinancialForecast, 'id' | 'business_plan_id' | 'user_id' | 'created_at' | 'updated_at'>;
}) {
  const { businessPlanId, userId, forecast } = args;
  return {
    business_plan_id: businessPlanId,
    user_id: userId,
    timeline: forecast.timeline,
    periods_count: forecast.periodsCount,
    start_date: forecast.startDate,
    assumptions: forecast.assumptions,
    revenue_streams: forecast.revenueStreams,
    expense_categories: forecast.expenseCategories,
    projections: forecast.projections,
    metrics: forecast.metrics,
    industry_benchmarks: forecast.industryBenchmarks || [],
    industry_name: null,
    last_calculated_at: forecast.last_calculated_at,
  };
}

/**
 * Calculate projections for a single period
 */
function calculatePeriodProjection(
  periodNumber: number,
  period: string,
  revenueStreams: RevenueStream[],
  expenseCategories: ExpenseCategory[],
  openingCashBalance: number,
  assumptions: FinancialAssumptions,
  openingContributedCapital: number,
  openingLoanBalance: number,
  periodMonths: number,
  capexSpend: number,
  depreciationExpense: number,
  interestExpense: number,
  principalRepayment: number,
  endingLoanBalance: number,
  openingAccountsReceivable: number,
  openingInventory: number,
  openingAccountsPayable: number,
  fixedAssetsGross: number,
  accumulatedDepreciation: number,
  retainedEarnings: number
): FinancialProjection {
  // Calculate total revenue for this period
  const revenueByStream: Record<string, number> = {};
  let totalRevenue = 0;
  
  revenueStreams.forEach(stream => {
    const periodRevenue = stream.projections[periodNumber - 1] || 0;
    revenueByStream[stream.id] = periodRevenue;
    totalRevenue += periodRevenue;
  });
  
  // Calculate total expenses for this period
  const expenseByCategory: Record<string, number> = {};
  let totalExpenses = 0;
  
  expenseCategories.forEach(category => {
    const periodExpense = category.projections[periodNumber - 1] || 0;
    expenseByCategory[category.id] = periodExpense;
    totalExpenses += periodExpense;
  });
  
  // Calculate profitability
  const operatingProfit = totalRevenue - totalExpenses - (depreciationExpense || 0);
  const profitBeforeTax = operatingProfit - (interestExpense || 0);
  const taxAmount = profitBeforeTax > 0 ? profitBeforeTax * (assumptions.taxRate / 100) : 0;
  const netProfit = profitBeforeTax - taxAmount;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  const periodDays = Math.max(1, periodMonths) * 30;
  const arDays = Math.max(0, assumptions.arDays || 0);
  const apDays = Math.max(0, assumptions.apDays || 0);
  const inventoryDays = Math.max(0, assumptions.inventoryDays || 0);

  const revenuePerDay = periodDays > 0 ? totalRevenue / periodDays : 0;
  const cogs = totalRevenue * (Math.max(0, assumptions.variableCostPercentage || 0) / 100);
  const cogsPerDay = periodDays > 0 ? cogs / periodDays : 0;

  const accountsReceivable = revenuePerDay * arDays;
  const inventory = cogsPerDay * inventoryDays;
  const accountsPayable = cogsPerDay * apDays;

  const netWorkingCapital = accountsReceivable + inventory - accountsPayable;
  const openingNetWorkingCapital = openingAccountsReceivable + openingInventory - openingAccountsPayable;
  const workingCapitalChange = netWorkingCapital - openingNetWorkingCapital;
  
  // Calculate cash flow
  const cashFlow = netProfit + (depreciationExpense || 0) - (capexSpend || 0) - (principalRepayment || 0) - (workingCapitalChange || 0);
  const cashBalance = openingCashBalance + cashFlow;

  const contributedCapital = openingContributedCapital;
  const netFixedAssets = (fixedAssetsGross || 0) - (accumulatedDepreciation || 0);
  const currentAssets = cashBalance + accountsReceivable + inventory;
  const currentLiabilities = accountsPayable;
  const totalAssets = currentAssets + netFixedAssets;
  const totalLiabilities = endingLoanBalance + accountsPayable;
  const totalEquity = contributedCapital + (retainedEarnings + netProfit);
  const balanceSheetTieOut = totalAssets - (totalLiabilities + totalEquity);
  
  // Check if break-even achieved
  const breakEven = netProfit >= 0;
  
  // Calculate burn rate (negative cash flow)
  const burnRate = cashFlow < 0 ? Math.abs(cashFlow) : 0;
  
  // Calculate runway (months of cash remaining at current burn rate)
  const runway = burnRate > 0 && cashBalance > 0 
    ? Math.floor((cashBalance / burnRate) * (periodMonths || 1)) 
    : undefined;
  
  return {
    period,
    periodNumber,
    totalRevenue,
    revenueByStream,
    totalExpenses,
    expenseByCategory,
    grossProfit: operatingProfit,
    netProfit,
    profitMargin,
    depreciationExpense,
    interestExpense,
    cashFlow,
    cumulativeCashFlow: cashBalance,
    capex: capexSpend,
    principalRepayment,
    accountsReceivable,
    accountsPayable,
    inventory,
    workingCapitalChange,
    currentAssets,
    currentLiabilities,
    cashBalance,
    fixedAssetsGross,
    accumulatedDepreciation,
    netFixedAssets,
    totalAssets,
    totalLiabilities,
    loanBalance: endingLoanBalance,
    totalEquity,
    retainedEarnings: retainedEarnings + netProfit,
    contributedCapital,
    balanceSheetTieOut,
    breakEven,
    burnRate: burnRate > 0 ? burnRate : undefined,
    runway
  };
}

function calculateMonthlyPayment(args: {
  principal: number;
  annualRatePct: number;
  termMonths: number;
}): number {
  const { principal, annualRatePct, termMonths } = args;
  const n = Math.max(0, termMonths);
  if (principal <= 0 || n <= 0) return 0;
  const r = Math.max(0, annualRatePct) / 100 / 12;
  if (r === 0) return principal / n;
  const denom = 1 - Math.pow(1 + r, -n);
  if (denom === 0) return 0;
  return (r * principal) / denom;
}

function getPeriodMonths(timeline: TimelineType, periodIndex0: number): number {
  if (timeline === 'monthly') return 1;
  if (timeline === 'quarterly') return 3;
  if (timeline === 'yearly') return 12;
  if (timeline === 'mixed') return periodIndex0 < 12 ? 1 : 12;
  return 1;
}

/**
 * Generate period labels based on timeline and start date
 */
function generatePeriodLabels(
  timeline: TimelineType,
  periodsCount: number,
  startDate: string
): string[] {
  const labels: string[] = [];
  const start = new Date(startDate);

  if (timeline === 'mixed') {
    const years = Math.max(0, periodsCount - 12);
    for (let i = 0; i < 12; i++) {
      const date = new Date(start);
      date.setMonth(start.getMonth() + i);
      labels.push(date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
    for (let y = 0; y < years; y++) {
      labels.push(`Year ${2 + y}`);
    }
    return labels;
  }
  
  for (let i = 0; i < periodsCount; i++) {
    if (timeline === 'yearly') {
      labels.push(`Year ${i + 1}`);
    } else if (timeline === 'quarterly') {
      const year = start.getFullYear() + Math.floor(i / 4);
      const quarter = (i % 4) + 1;
      labels.push(`Q${quarter} ${year}`);
    } else { // monthly
      const date = new Date(start);
      date.setMonth(start.getMonth() + i);
      labels.push(date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
  }
  
  return labels;
}

/**
 * Project revenue/expenses with growth rate
 */
function projectValues(
  initialValue: number,
  growthRate: number,
  periodsCount: number,
  scenarioMultiplier: number = 1.0
): number[] {
  const values: number[] = [];
  const adjustedGrowthRate = growthRate * scenarioMultiplier;
  
  for (let i = 0; i < periodsCount; i++) {
    const value = initialValue * Math.pow(1 + (adjustedGrowthRate / 100), i);
    values.push(Math.round(value));
  }
  
  return values;
}

function projectValuesMixed(args: {
  initialValue: number;
  growthRate: number;
  yearsAfterMonth12: number;
  scenarioMultiplier?: number;
}): number[] {
  const {
    initialValue,
    growthRate,
    yearsAfterMonth12,
    scenarioMultiplier = 1.0,
  } = args;

  const months = 12 + Math.max(0, yearsAfterMonth12) * 12;
  const monthly = projectValues(initialValue, growthRate, months, scenarioMultiplier);

  const mixed: number[] = [];
  for (let i = 0; i < 12; i++) {
    mixed.push(monthly[i] ?? 0);
  }
  for (let y = 0; y < yearsAfterMonth12; y++) {
    const startIdx = 12 + y * 12;
    const endIdx = startIdx + 12;
    const sum = monthly.slice(startIdx, endIdx).reduce((acc, v) => acc + (v ?? 0), 0);
    mixed.push(Math.round(sum));
  }

  return mixed;
}

/**
 * Calculate all projections for a scenario
 */
function calculateScenarioProjections(
  input: ForecastCalculationInput,
  scenarioType: ScenarioType
): FinancialProjection[] {
  const scenarioMultipliers = {
    realistic: 1.0,
    optimistic: 1.3, // 30% better
    pessimistic: 0.7 // 30% worse
  };
  
  const multiplier = scenarioMultipliers[scenarioType];
  const periodLabels = generatePeriodLabels(input.timeline, input.periodsCount, input.startDate);

  const yearsAfterMonth12 = input.timeline === 'mixed' ? Math.max(0, input.periodsCount - 12) : 0;
  
  // Project revenue streams
  const adjustedRevenueStreams: RevenueStream[] = input.revenueStreams.map(stream => ({
    ...stream,
    projections: input.timeline === 'mixed'
      ? projectValuesMixed({
          initialValue: stream.projections[0] || stream.unitPrice! * stream.unitsPerPeriod! || 0,
          growthRate: stream.growthRate,
          yearsAfterMonth12,
          scenarioMultiplier: multiplier,
        })
      : projectValues(
          stream.projections[0] || stream.unitPrice! * stream.unitsPerPeriod! || 0,
          stream.growthRate,
          input.periodsCount,
          multiplier
        )
  }));
  
  // Project expense categories
  const adjustedExpenseCategories: ExpenseCategory[] = input.expenseCategories.map(category => ({
    ...category,
    projections: input.timeline === 'mixed'
      ? projectValuesMixed({
          initialValue: category.projections[0] || 0,
          growthRate: category.growthRate || 0,
          yearsAfterMonth12,
          scenarioMultiplier: category.category === 'fixed' ? 1.0 : multiplier,
        })
      : projectValues(
          category.projections[0] || 0,
          category.growthRate || 0,
          input.periodsCount,
          category.category === 'fixed' ? 1.0 : multiplier // Fixed costs don't scale with scenarios
        )
  }));
  
  // Calculate projections for each period
  const projections: FinancialProjection[] = [];
  const openingContributedCapital = input.assumptions.investmentAmount || 0;
  const openingLoanBalance = input.assumptions.loanAmount || 0;
  let cashBalance = openingContributedCapital + openingLoanBalance;

  const loanAnnualRatePct = input.assumptions.interestRate || 0;
  const loanTermMonths = input.assumptions.loanTermMonths || 0;
  const loanMonthlyPayment = calculateMonthlyPayment({
    principal: openingLoanBalance,
    annualRatePct: loanAnnualRatePct,
    termMonths: loanTermMonths,
  });

  let loanBalance = openingLoanBalance;

  let accountsReceivable = 0;
  let inventory = 0;
  let accountsPayable = 0;

  let retainedEarnings = 0;
  let fixedAssetsGross = 0;
  let accumulatedDepreciation = 0;

  const capexItems = input.assumptions.capexItems || [];
  let assets: CapexAssetState[] = [];
  
  for (let i = 0; i < input.periodsCount; i++) {
    const periodMonths = getPeriodMonths(input.timeline, i);

    // Debt schedule (amortizing, calculated monthly then aggregated to the period)
    let interestExpense = 0;
    let principalRepayment = 0;

    if (loanBalance > 0 && loanMonthlyPayment > 0 && loanAnnualRatePct >= 0) {
      const monthlyRate = loanAnnualRatePct / 100 / 12;
      for (let m = 0; m < periodMonths; m++) {
        if (loanBalance <= 0) break;
        const monthlyInterest = loanBalance * monthlyRate;
        const payment = Math.min(loanMonthlyPayment, loanBalance + monthlyInterest);
        const monthlyPrincipal = Math.max(0, payment - monthlyInterest);

        interestExpense += monthlyInterest;
        principalRepayment += monthlyPrincipal;
        loanBalance -= monthlyPrincipal;
      }
    }

    loanBalance = Math.max(0, loanBalance);

    const capexSpend = capexItems
      .filter((c) => c.purchasePeriodNumber === i + 1)
      .reduce((acc, c) => acc + (c.amount || 0), 0);

    const purchases = capexItems.filter((c) => c.purchasePeriodNumber === i + 1);
    for (const p of purchases) {
      const usefulLifeMonths = Math.max(1, p.usefulLifeMonths || 1);
      assets.push({
        id: p.id,
        name: p.name,
        amount: p.amount || 0,
        monthlyDepreciation: (p.amount || 0) / usefulLifeMonths,
        remainingMonths: usefulLifeMonths,
      });
    }

    let depreciationExpense = 0;
    for (const a of assets) {
      if (a.remainingMonths <= 0) continue;
      const depMonths = Math.min(periodMonths, a.remainingMonths);
      depreciationExpense += a.monthlyDepreciation * depMonths;
      a.remainingMonths -= depMonths;
    }

    fixedAssetsGross += capexSpend;
    accumulatedDepreciation += depreciationExpense;

    const projection = calculatePeriodProjection(
      i + 1,
      periodLabels[i],
      adjustedRevenueStreams,
      adjustedExpenseCategories,
      cashBalance,
      input.assumptions,
      openingContributedCapital,
      openingLoanBalance,
      periodMonths,
      capexSpend,
      depreciationExpense,
      interestExpense,
      principalRepayment,
      loanBalance,
      accountsReceivable,
      inventory,
      accountsPayable,
      fixedAssetsGross,
      accumulatedDepreciation,
      retainedEarnings
    );
    
    projections.push(projection);

    cashBalance = projection.cashBalance ?? cashBalance;
    retainedEarnings = projection.retainedEarnings ?? retainedEarnings;

    accountsReceivable = projection.accountsReceivable ?? accountsReceivable;
    inventory = projection.inventory ?? inventory;
    accountsPayable = projection.accountsPayable ?? accountsPayable;
  }
  
  return projections;
}

/**
 * Calculate financial metrics from projections
 */
function calculateMetrics(
  realisticProjections: FinancialProjection[],
  assumptions: FinancialAssumptions,
  timeline: TimelineType
): FinancialMetrics {
  const lastPeriod = realisticProjections[realisticProjections.length - 1];
  const firstPeriod = realisticProjections[0];
  
  // Calculate average margins
  const avgGrossMargin = realisticProjections.reduce((sum, p) => 
    sum + (p.totalRevenue > 0 ? (p.grossProfit / p.totalRevenue) * 100 : 0), 0
  ) / realisticProjections.length;
  
  const avgNetMargin = realisticProjections.reduce((sum, p) => 
    sum + p.profitMargin, 0
  ) / realisticProjections.length;
  
  // Calculate revenue growth rate
  const revenueGrowthRate = firstPeriod.totalRevenue > 0
    ? ((lastPeriod.totalRevenue - firstPeriod.totalRevenue) / firstPeriod.totalRevenue) * 100
    : 0;
  
  // Find break-even point
  const breakEvenPeriod = realisticProjections.findIndex(p => p.breakEven);
  const monthsToBreakEven = (() => {
    if (breakEvenPeriod < 0) return realisticProjections.length;
    if (timeline !== 'mixed') return breakEvenPeriod + 1;
    if (breakEvenPeriod < 12) return breakEvenPeriod + 1;
    const yearlyPeriods = breakEvenPeriod - 12 + 1;
    return 12 + yearlyPeriods * 12;
  })();
  
  // Calculate break-even revenue (simplified)
  const fixedCosts = assumptions.fixedCosts;
  const variableCostRatio = assumptions.variableCostPercentage / 100;
  const breakEvenRevenue = fixedCosts > 0 
    ? fixedCosts / (1 - variableCostRatio)
    : 0;

  const currentAssets = lastPeriod.currentAssets ?? 0;
  const currentLiabilities = lastPeriod.currentLiabilities ?? 0;
  const cash = lastPeriod.cashBalance ?? 0;
  const ar = lastPeriod.accountsReceivable ?? 0;
  const inventory = lastPeriod.inventory ?? 0;

  const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : undefined;
  const quickRatio = currentLiabilities > 0 ? (cash + ar) / currentLiabilities : undefined;

  const ebitda = (lastPeriod.totalRevenue ?? 0) - (lastPeriod.totalExpenses ?? 0);
  const debtService = (lastPeriod.interestExpense ?? 0) + (lastPeriod.principalRepayment ?? 0);
  const dscr = debtService > 0 ? ebitda / debtService : undefined;
  
  return {
    grossMargin: Math.round(avgGrossMargin * 10) / 10,
    netMargin: Math.round(avgNetMargin * 10) / 10,
    ebitda: Math.round(ebitda),
    currentRatio: currentRatio !== undefined ? Math.round(currentRatio * 100) / 100 : undefined,
    quickRatio: quickRatio !== undefined ? Math.round(quickRatio * 100) / 100 : undefined,
    dscr: dscr !== undefined ? Math.round(dscr * 100) / 100 : undefined,
    revenueGrowthRate: Math.round(revenueGrowthRate * 10) / 10,
    breakEvenRevenue: Math.round(breakEvenRevenue),
    monthsToBreakEven
  };
}

/**
 * Generate industry benchmarks (mock data for now)
 * In production, this would call an external API or database
 */
function generateIndustryBenchmarks(
  industry: string | undefined,
  metrics: FinancialMetrics
): IndustryBenchmark[] {
  if (!industry) return [];
  
  // Mock benchmark data - in production, fetch from database/API
  const benchmarks: IndustryBenchmark[] = [
    {
      metricName: 'Gross Margin',
      yourValue: metrics.grossMargin,
      industryAverage: 45,
      industryMedian: 42,
      topQuartile: 60,
      unit: '%',
      status: metrics.grossMargin >= 45 ? 'above' : metrics.grossMargin >= 35 ? 'average' : 'below',
      recommendation: metrics.grossMargin < 45 
        ? 'Consider increasing prices or reducing direct costs to improve gross margin.'
        : undefined
    },
    {
      metricName: 'Net Margin',
      yourValue: metrics.netMargin,
      industryAverage: 15,
      industryMedian: 12,
      topQuartile: 25,
      unit: '%',
      status: metrics.netMargin >= 15 ? 'above' : metrics.netMargin >= 8 ? 'average' : 'below',
      recommendation: metrics.netMargin < 15 
        ? 'Focus on operational efficiency to improve net profit margin.'
        : undefined
    },
    {
      metricName: 'Revenue Growth Rate',
      yourValue: metrics.revenueGrowthRate,
      industryAverage: 20,
      industryMedian: 18,
      topQuartile: 35,
      unit: '%',
      status: metrics.revenueGrowthRate >= 20 ? 'above' : metrics.revenueGrowthRate >= 10 ? 'average' : 'below'
    }
  ];
  
  return benchmarks;
}

/**
 * Main function to calculate complete financial forecast
 */
export function calculateFinancialForecast(
  input: ForecastCalculationInput
): Omit<FinancialForecast, 'id' | 'business_plan_id' | 'user_id' | 'created_at' | 'updated_at'> {
  // Calculate projections for all three scenarios
  const projections: ScenarioProjections = {
    realistic: calculateScenarioProjections(input, 'realistic'),
    optimistic: calculateScenarioProjections(input, 'optimistic'),
    pessimistic: calculateScenarioProjections(input, 'pessimistic')
  };
  
  // Calculate metrics based on realistic scenario
  const metrics = calculateMetrics(projections.realistic, input.assumptions, input.timeline);
  
  // Generate industry benchmarks
  const industryBenchmarks = generateIndustryBenchmarks(input.industry, metrics);
  
  return {
    timeline: input.timeline,
    periodsCount: input.periodsCount,
    startDate: input.startDate,
    assumptions: input.assumptions,
    revenueStreams: input.revenueStreams,
    expenseCategories: input.expenseCategories,
    projections,
    metrics,
    industryBenchmarks,
    last_calculated_at: new Date().toISOString()
  };
}

/**
 * Save forecast to Supabase
 */
export async function saveForecast(
  businessPlanId: string,
  userId: string,
  forecast: Omit<FinancialForecast, 'id' | 'business_plan_id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<FinancialForecast | null> {
  try {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('Cannot save forecast - user not authenticated');
      return null;
    }

    // Verify the authenticated user matches the userId being saved
    if (session.user.id !== userId) {
      console.error('Cannot save forecast - user ID mismatch');
      return null;
    }

    const { data, error } = await supabase
      .from('financial_forecasts')
      .upsert(toForecastUpsertRow({ businessPlanId, userId, forecast }))
      .select('*')
      .single();
    
    if (error) {
      console.error('Error saving forecast:', error);
      return null;
    }
    
    return mapForecastRow(data);
  } catch (error) {
    console.error('Error saving forecast:', error);
    return null;
  }
}

/**
 * Get forecast for a business plan
 */
export async function getForecast(businessPlanId: string): Promise<FinancialForecast | null> {
  try {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('Cannot fetch forecast - user not authenticated');
      return null;
    }

    const { data, error } = await supabase
      .from('financial_forecasts')
      .select('*')
      .eq('business_plan_id', businessPlanId)
      .maybeSingle();  // ✅ Returns null if no rows found, doesn't throw error
    
    if (error) {
      console.error('Error fetching forecast:', error);
      return null;
    }
    
    return data ? mapForecastRow(data) : null;
  } catch (error) {
    console.error('Unexpected error fetching forecast:', error);
    return null;
  }
}

/**
 * Delete forecast
 */
export async function deleteForecast(forecastId: string): Promise<boolean> {
  try {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('Cannot delete forecast - user not authenticated');
      return false;
    }

    const { error } = await supabase
      .from('financial_forecasts')
      .delete()
      .eq('id', forecastId);
    
    return !error;
  } catch (error) {
    console.error('Error deleting forecast:', error);
    return false;
  }
}
